import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useToast, useUIStore } from '@/stores/ui.store';
import { useHaptics } from '@/hooks/useHaptics';
import { authFetch, isLoggedIn } from '@/lib/auth-client';
import { refreshBalance } from '@/lib/balance';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { scheduleUndercardNotifications } from '@/lib/notifications';
import type {
  UndercardDetail,
  GameData,
  PropData,
  GamePickData,
  MyPropData,
  MyEntryDetail,
  SurvivorStatusData,
  SurvivorPickInfo,
  SurvivorUsedTeam,
  WeeklyScoreData,
  MyRosterData,
  WeeklyLineupData,
} from '@/services/events/main-event';
import { ShareCardButton } from '@/components/rewards/ShareCardButton';
import type { PickSelection } from '@prisma/client';
import { CountdownTimer, LineRefreshCountdown, ScoreBanner, PurchasedPropsSummary, StandalonePropCard, InlineManualPropRow } from './widgets';
import { UnifiedGameCard } from './game-card';
import { SurvivorStatusBanner } from './SurvivorStatusBanner';
import { UsedTeamsStrip } from './UsedTeamsStrip';
import { RosterWeekPanel } from './RosterWeekPanel';
import { LineupEditor, LineupSaveError } from './LineupEditor';
import { getAbbrev } from './utils';
import {
  isStaleContext,
  isGameLockedAt,
  mergeWeekCacheEntry,
  isCacheHit,
  savedPicksByGame,
  mergeSavedPicks,
  reconcileDraftsAfterSave,
  reconcileSkippedDrafts,
  snapLockedDraftsToSaved,
  hasUnsavedGamePicks,
  hasUnsavedSurvivorPick,
  effectivePickedGameIds,
  survivorPickedGameIds,
  resolveSavedPickForRound,
  resolveSurvivorDraft,
  snapLockedWeekPick,
} from './week-state';
import type { WeekCacheEntry } from './week-state';
import { markFirstMilestoneOnce } from '@/lib/first-milestone';


/** Props for the match track. Every week — open or scored — uses the carousel. */
interface MatchTrackProps {
  games: GameData[];
  renderCard: (game: GameData) => React.ReactNode;
  /** Games that already carry a pick (draft or saved) — the mini-rail's complete state. */
  pickedGameIds?: ReadonlySet<string>;
}

/**
 * Horizontal scroll-snap track of match cards with a per-match helmet mini-rail
 * below it. The rail is DOTS, not team marks: a helmet
 * pair repeated 16 times competed with the match card directly above it, which
 * already names both teams at full size. The rail's job is position and pick
 * progress, not identity.
 *
 * This is not a return to the original dot stepper, which failed because its
 * 6px dot was also its 6px hit box. Here the button is the 44x24 target and
 * the dot is only the mark inside it.
 *
 * The active chip tracks the card nearest the track's left edge as you scroll,
 * and tapping a chip jumps the carousel to that match.
 *
 * Fine-pointer devices additionally get prev/next arrows over the track: touch
 * and trackpads swipe a hidden-scrollbar scroller natively, but a plain mouse
 * has no gesture for one — without the arrows a desktop user simply couldn't
 * scroll the slate.
 */
/**
 * How far the slate sits from the screen edge, and the one place that says so.
 *
 * The negative margin is the PAGE GUTTER and nothing else: the container is
 * px-4 / sm:px-6, and pulling wider than that would push the track past the
 * viewport edge and give the whole page a horizontal scrollbar. Distance from
 * the edge is the padding's job — deliberately larger than the gutter, so the
 * slate sits inset from the page's own content.
 *
 * The loading ghost renders a card at these widths before the real track
 * exists, and `w-[92%]` is a percentage of the PADDED box: a ghost with a
 * different inset does not just sit in the wrong place, it is a different size
 * from the card that replaces it — and the page skeleton hands over to that
 * ghost, so it is the same card a third time. Every one of them takes this.
 */
export const CAROUSEL_INSET = '-mx-4 px-8 sm:-mx-6 sm:px-10';

function MatchCarousel({ games, renderCard, pickedGameIds }: MatchTrackProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();
  const { fire } = useHaptics();

  // Whether the track can scroll each way — drives the arrows' visibility.
  // Measured from scroll metrics rather than derived from `active`: the right
  // edge depends on how many cards fit the viewport, which only the metrics
  // know. The 12px slack absorbs a snap resting position that is a shade off
  // zero: percentage card widths land on fractional pixels, and a tighter
  // threshold would show the left arrow on an untouched track. Scroll-padding
  // matches the track's own padding, so the rest position is otherwise 0.
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  const measureEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScroll({
      left: track.scrollLeft > 12,
      right: track.scrollLeft < track.scrollWidth - track.clientWidth - 12,
    });
  }, []);

  // Index of the card nearest the track's left edge.
  const onScroll = () => {
    measureEdges();
    const track = trackRef.current;
    if (!track) return;
    // The first card's offsetLeft IS the track's leading padding, so the
    // comparison stays honest whatever that padding is at this breakpoint —
    // where a hardcoded inset silently skews every distance once it changes.
    const inset = (track.children[0] as HTMLElement | undefined)?.offsetLeft ?? 0;
    const left = track.scrollLeft + inset;
    let best = 0;
    let bestDist = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const d = Math.abs((child as HTMLElement).offsetLeft - left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  };

  const goTo = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const el = track.children[i] as HTMLElement | undefined;
    el?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  };

  // A week swap reuses this component rather than remounting it whenever
  // WeekContentFade is inert (reduced motion returns bare children, so React
  // reconciles by position), which strands `active` on the old week's index:
  // going from a 16-game week scrolled to game 13 to a 4-game week left
  // active=12, chipRefs.current[12] null, the centring effect below early-
  // returning, and NO chip marked active — the whole strip looked dead. Reset
  // both the index and the track so a week swap always lands on game 1, which
  // is what the animated path gets for free from its remount.
  const gamesKey = games.map((g) => g.id).join(',');
  useEffect(() => {
    setActive(0);
    trackRef.current?.scrollTo({ left: 0, behavior: 'auto' });
    // The rail too, explicitly. When `active` is ALREADY 0 React bails on
    // setActive(0), so the centring effect below never re-runs — and on the
    // reduced-motion path the carousel is reused rather than remounted, so a
    // rail scrolled sideways on the previous week would keep that offset and
    // hide game 1's chip.
    railRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [gamesKey]);

  // Arrow visibility on mount, week swap, and viewport resize. Scrolling keeps
  // it current via onScroll; this covers the states no scroll event announces
  // (initial overflow, and a resize that changes how many cards fit).
  useEffect(() => {
    measureEdges();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measureEdges);
    ro.observe(track);
    return () => ro.disconnect();
  }, [gamesKey, measureEdges]);

  // Keep the active chip centred as the carousel moves, so the rail behaves
  // like a filmstrip instead of stranding the current game off-screen on a
  // 16-game slate. Manual scrollLeft rather than scrollIntoView: the latter can
  // scroll the whole page vertically on its way to satisfying the request.
  //
  // Measured off rects rather than offsetLeft: the rail is the chips'
  // offsetParent *today*, but any positioned wrapper slipped between them
  // would silently rebase offsetLeft and push every chip off centre. Rects are
  // absolute, so the delta is correct whatever the DOM in between looks like.
  // clientLeft/clientWidth describe the padding box — the actual scrollport —
  // which is what the chip has to be centred against.
  useEffect(() => {
    const rail = railRef.current;
    const chip = chipRefs.current[active];
    if (!rail || !chip) return;
    if (rail.scrollWidth <= rail.clientWidth) return; // whole strip already visible
    const railBox = rail.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    const viewportCentre = railBox.left + rail.clientLeft + rail.clientWidth / 2;
    const delta = chipBox.left + chipBox.width / 2 - viewportCentre;
    rail.scrollTo({
      left: Math.max(0, rail.scrollLeft + delta),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [active, reduceMotion]);

  if (games.length === 0) return null;

  return (
    <div>
      <div className="relative">
        <div
          ref={trackRef}
          onScroll={onScroll}
          className={cn(
            'flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2',
            CAROUSEL_INSET,
            // Must equal the padding at every breakpoint. When it is smaller,
            // mandatory snap rests at (padding - scroll-padding) instead of 0,
            // which reads as a scrolled track and shows the left arrow on an
            // untouched slate.
            'scroll-pl-8 sm:scroll-pl-10',
          )}
        >
          {games.map((game) => (
            <div key={game.id} className="snap-start shrink-0 w-[92%] max-w-[380px]">
              {renderCard(game)}
            </div>
          ))}
        </div>

        {/* Mouse affordance — `pointer-fine` only, so touch layouts never see
            them. Rendered only while that direction can actually scroll, which
            doubles as the end-of-track state.

            Each arrow is a STICKY button inside a full-height rail, not a box
            centred on the track. Centring on the track centres on the content,
            and the content is unbounded: one card with its player props open
            takes the track past 3000px, which put the arrows thousands of
            pixels below the slate they scroll. `top: 50vh` holds them in the
            middle of the WINDOW instead, and the rail — the only thing that
            still spans the track — clamps them to its top and bottom edges as
            the track enters and leaves the screen.

            The rail is therefore as tall as the card, so it must not take
            pointer events; only the button does. Otherwise an invisible 36px
            column would shadow the card's edge for the card's whole length. */}
        {canScroll.left && (
          <div className="hidden pointer-fine:block absolute inset-y-0 -left-3 w-9 z-10 pointer-events-none">
            <button
              type="button"
              onClick={() => { fire('selection'); goTo(Math.max(0, active - 1)); }}
              aria-label="Previous game"
              className={cn(
                // -1.125rem = half the button, so 50vh is its centre.
                'sticky top-[calc(50vh-1.125rem)] pointer-events-auto',
                'flex h-9 w-9 items-center justify-center rounded-full',
                'bg-black/60 border border-white/15 text-white/80 backdrop-blur-sm',
                'transition-colors hover:text-white hover:border-gold/50',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
              )}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}
        {canScroll.right && (
          <div className="hidden pointer-fine:block absolute inset-y-0 -right-3 w-9 z-10 pointer-events-none">
            <button
              type="button"
              onClick={() => { fire('selection'); goTo(Math.min(games.length - 1, active + 1)); }}
              aria-label="Next game"
              className={cn(
                'sticky top-[calc(50vh-1.125rem)] pointer-events-auto',
                'flex h-9 w-9 items-center justify-center rounded-full',
                'bg-black/60 border border-white/15 text-white/80 backdrop-blur-sm',
                'transition-colors hover:text-white hover:border-gold/50',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
              )}
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {/* Per-match helmet mini-rail — below the track (wireframe placement).
          `relative` makes this the chips' offsetParent, which the centring
          effect above measures against. `w-max mx-auto` centres a short slate
          (2-3 games) without clipping a long one: once the content outgrows the
          container the auto margins resolve to 0 and it simply scrolls. */}
      {games.length > 1 && (
        <div
          ref={railRef}
          className="relative mt-3 -mx-4 sm:-mx-6 overflow-x-auto overscroll-x-contain scrollbar-hide"
        >
          <div className="flex w-max mx-auto py-1">
            {games.map((game, i) => {
              const isActive = i === active;
              const picked = pickedGameIds?.has(game.id) ?? false;
              return (
                <button
                  key={game.id}
                  ref={(el) => { chipRefs.current[i] = el; }}
                  type="button"
                  onClick={() => { fire('selection'); goTo(i); }}
                  aria-label={`Go to ${game.awayTeam} at ${game.homeTeam}${picked ? ', picked' : ''}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'group relative shrink-0 h-11 w-6',
                    'flex items-center justify-center',
                    'focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50 rounded-md',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'block rounded-full transition-all duration-200',
                      isActive ? 'h-2 w-4' : 'h-2 w-2',
                      picked ? 'bg-gold' : 'bg-warm-gray/35 group-hover:bg-warm-gray/60',
                      isActive && !picked && 'ring-1 ring-gold/70',
                    )}
                  />
                  </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Week-swap crossfade over the week's CONTENT only.
 *
 * This lives inside WeekView rather than around it on purpose: WeekView holds
 * the week cache and the unsaved draft map, so a keyed wrapper at the page
 * level remounted the whole component on every week change and threw both away.
 * Keying the content region instead swaps what the user sees while the
 * component (and its state) persists.
 *
 * Two hard rules:
 *  1. OPACITY ONLY. The action bar is `position: fixed`; any
 *     transform/filter/perspective on an ancestor would make that ancestor the
 *     bar's containing block and the bar would scroll/slide with the week.
 *  2. The action bar must stay OUTSIDE this wrapper (a sibling, never a
 *     descendant) — see the render below.
 */
function WeekContentFade({
  weekKey,
  children,
}: {
  weekKey: string;
  children: React.ReactNode;
}) {
  const reducedMotion = useReducedMotion();
  if (reducedMotion) return <>{children}</>;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={weekKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.16, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}


export interface WeekViewProps {
  eventId: string;
  /** The selected week's undercard id — switching weeks swaps this prop. */
  undercardId: string;
  isSurvivor: boolean;
  /** BEST_BALL third mode: roster panel + roster-filtered props, no picks. */
  isBestBall?: boolean;
  /** DRAFT_PPR: card-league mode + a pre-lock weekly LineupEditor. */
  isDraftPpr?: boolean;
  sport: string;
  /** Event-level entry config (entry is event-scoped, not per-week). */
  accessType: string;
  entryFee: number;
  /** Event-level join eligibility (any round still has an open game). */
  joinable: boolean;
  onJoinableChange: (joinable: boolean) => void;
  hasEntered: boolean;
  /** Parent-owned entry detail — per-week scores + share-card entry ids. */
  myEntry: MyEntryDetail | null;
  /** Parent refetches my-entry (and claims); awaited after enter/save. */
  refreshEntry: () => Promise<void>;
  /** Parent-owned survivor my-status (null for STANDARD or logged-out). */
  survivorStatus: SurvivorStatusData | null;
  onSurvivorStatusChange: (st: SurvivorStatusData | null) => void;
  /** Live draft-pick count for the week rail's progress dashes. */
  onDraftCountChange?: (undercardId: string, count: number) => void;
  /**
   * The action bar's own Enter succeeded — the page fires its entry
   * celebration (toast + gold ring). Optional: WeekView still works without it.
   */
  onEntered?: () => void;
}

/**
 * The body of a single week on the event page: countdown, banners, pick
 * carousel (or vertical results list once scored), props, and the floating
 * action bar. This is the old undercard page's logic relocated — the event
 * page renders the header + week rail and swaps this component's undercardId.
 */
export function WeekView({
  eventId,
  undercardId,
  isSurvivor,
  isBestBall = false,
  isDraftPpr = false,
  sport,
  accessType,
  entryFee,
  joinable,
  onJoinableChange,
  hasEntered,
  myEntry,
  refreshEntry,
  survivorStatus,
  onSurvivorStatusChange,
  onDraftCountChange,
  onEntered,
}: WeekViewProps) {
  const router = useRouter();
  const { accessToken } = useAuthStore();
  // Transient feedback goes through the global toast system (haptics included).
  const { success, error: showError, warning } = useToast();
  // Press-time haptic for Save — the landing toast fires its own 'success'.
  const { fire } = useHaptics();
  const [undercard, setUndercard] = useState<UndercardDetail | null>(null);
  const [draftPicks, setDraftPicks] = useState<Map<string, PickSelection>>(new Map());
  const [savedPicks, setSavedPicks] = useState<GamePickData[]>([]);
  const [myProps, setMyProps] = useState<MyPropData[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [entering, setEntering] = useState(false);
  const [purchasingPropId, setPurchasingPropId] = useState<string | null>(null);
  // Synchronous guard so only one prop purchase can start before React
  // re-renders the controls into their pending state.
  const inFlightProps = useRef<Set<string>>(new Set());
  const [maxProps, setMaxProps] = useState(5);
  const [purchasedCount, setPurchasedCount] = useState(0);
  const [accessCode, setAccessCode] = useState('');
  // SURVIVOR format — one pick per week, no props. weekPick is the draft for
  // THIS undercard (saved truth lives in survivorStatus.picksByWeek).
  const [weekPick, setWeekPick] = useState<{ gameId: string; side: 'HOME' | 'AWAY' } | null>(null);
  // A survivor pick the server has ACCEPTED this session, keyed by round.
  // my-status is the usual saved truth, but an entered user whose my-status
  // fetch failed has none — this is what keeps a 200 reading as "saved" there
  // instead of leaving the bar dirty (see savedPickForRound).
  const [confirmedWeekPick, setConfirmedWeekPick] = useState<{
    undercardId: string;
    gameId: string;
    side: 'HOME' | 'AWAY';
  } | null>(null);
  // Card-league third mode (BEST_BALL / DRAFT_PPR) — roster-filtered
  // predictions + the week's lineup. bbWeek = scored rows (weekly-scores);
  // bbRoster = Best Ball preview before scoring; lineup = the DRAFT_PPR
  // chosen-or-default lineup (drives the editor pre-lock, the frozen panel after).
  const [bbProps, setBbProps] = useState<PropData[]>([]);
  const [bbWeek, setBbWeek] = useState<WeeklyScoreData | null>(null);
  const [bbRoster, setBbRoster] = useState<MyRosterData | null>(null);
  const [lineup, setLineup] = useState<WeeklyLineupData | null>(null);

  // One flag for every branch the two card-league formats share.
  const isCardLeague = isBestBall || isDraftPpr;

  // Visited weeks, keyed by undercardId — hydrated synchronously on switch so
  // flipping back to a week the user already loaded is instant.
  const cacheRef = useRef<Map<string, WeekCacheEntry>>(new Map());

  // Entry state readable inside fetchData without making it a dependency
  // (BEST_BALL gates its my-roster fetch on this).
  const hasEnteredRef = useRef(hasEntered);

  // The week currently on screen, readable from async callbacks. This component
  // stays mounted across week switches, so anything that resolves after an
  // await has to prove it's still talking about the week it started on before
  // it touches state (the cache is keyed and is always safe to write).
  const undercardIdRef = useRef(undercardId);
  useEffect(() => {
    undercardIdRef.current = undercardId;
  }, [undercardId]);

  // Same guard one level up. The page stays mounted across route-param
  // changes, so WeekView is re-rendered with a new eventId rather than
  // remounted — an in-flight survivor request from the previous event would
  // otherwise resolve and hand that event's status to the new one, corrupting
  // burned teams and the rail glyphs.
  const eventIdRef = useRef(eventId);
  useEffect(() => {
    eventIdRef.current = eventId;
  }, [eventId]);

  // Latest committed savedPicks. Async save handlers must not fold their
  // accepted picks into the render closure's copy — a background reconcile can
  // land while the POST is in flight and that merge would silently undo it.
  const savedPicksRef = useRef(savedPicks);
  useEffect(() => {
    savedPicksRef.current = savedPicks;
  }, [savedPicks]);

  // ── Action-bar footprint ──────────────────────────────────────────────────
  // The bar is viewport-fixed, and so is the toast stack: on mobile the toasts
  // used to land ON the bar and swallow Save / "All saved" / Clear for 3-5s.
  // Publishing the bar's footprint on the document root lets the toast column
  // offset itself by exactly that much (see ui/toast.tsx) without either
  // component importing the other.
  //
  // Measured as "viewport floor → bar's top edge" rather than plain offsetHeight
  // because the bar isn't flush with the bottom here — it is parked above the
  // 4rem bottom tab bar, and that whole span is what an overlay has to clear.
  //
  // A callback ref rather than an effect: showActionBar is computed below the
  // loading/not-found early returns, so no hook can depend on it — and React
  // invokes the ref with null when the bar unmounts OR when WeekView does, which
  // is precisely when the property must go away so other pages' toasts drop back.
  const actionBarCleanup = useRef<(() => void) | null>(null);
  const actionBarRef = useCallback((el: HTMLDivElement | null) => {
    actionBarCleanup.current?.();
    actionBarCleanup.current = null;
    const root = document.documentElement;
    if (!el) {
      root.style.removeProperty('--action-bar-h');
      return;
    }
    const publish = () => {
      const { top } = el.getBoundingClientRect();
      const footprint = Math.max(0, Math.round(window.innerHeight - top));
      root.style.setProperty('--action-bar-h', `${footprint}px`);
    };
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    window.addEventListener('resize', publish);
    actionBarCleanup.current = () => {
      ro?.disconnect();
      window.removeEventListener('resize', publish);
    };
  }, []);
  useEffect(
    () => () => {
      // Belt-and-braces: the ref callback above already fires with null on
      // unmount, but a stray property here would push every other page's toasts.
      actionBarCleanup.current?.();
      actionBarCleanup.current = null;
      document.documentElement.style.removeProperty('--action-bar-h');
    },
    [],
  );

  // Per-week entry scores from the parent-owned my-entry.
  const ucEntry = myEntry?.undercardEntries.find((ue) => ue.undercardId === undercardId) ?? null;
  const myEntryData = ucEntry
    ? {
        undercardEntryId: ucEntry.id,
        pickScore: ucEntry.pickScore,
        propBonusScore: ucEntry.propBonusScore,
        totalScore: ucEntry.totalScore,
        accuracyModifier: ucEntry.accuracyModifier ?? 1.0,
      }
    : null;

  // Live tick — re-render every 1s so per-game lock state (computed from nowMs)
  // flips cards to Locked at their lock time without a manual refresh, in step
  // with the 1s CountdownTimer (a coarser tick would let a card stay pickable for
  // up to the interval after the countdown already shows locked).
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const i = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // When the live tick locks a game, snap its draft pick back to the saved truth so a
  // locked card never shows a selection that was never persisted (keeps the action
  // bar's unsaved state honest). Mirrors the skipped-games reconcile after a save.
  useEffect(() => {
    if (!undercard) return;
    setDraftPicks((prev) => snapLockedDraftsToSaved(prev, undercard, savedPicks, nowMs));
  }, [nowMs, undercard, savedPicks]);

  // Saved survivor pick for the round being viewed (server truth from my-status).
  const savedWeekPickInfo = isSurvivor
    ? survivorStatus?.picksByWeek.find((p) => p.undercardId === undercardId) ?? null
    : null;
  /**
   * The saved pick for a round, as the draft comparison sees it: the my-status
   * row when there is one, otherwise a pick the server accepted this session.
   * The fallback is what covers "entered + my-status null" — that combination
   * gets no optimistic status write (writing a shell there wipes usedTeams), so
   * without it a 200 would still read as unsaved and strand the Save button.
   */
  const savedPickForRound = useCallback(
    (roundUndercardId: string): { gameId: string; side: 'HOME' | 'AWAY' } | null =>
      resolveSavedPickForRound(survivorStatus?.picksByWeek, confirmedWeekPick, roundUndercardId),
    [survivorStatus, confirmedWeekPick],
  );
  const savedWeekPick: { gameId: string; side: 'HOME' | 'AWAY' } | null = isSurvivor
    ? savedPickForRound(undercardId)
    : null;

  // Sync the survivor draft with the server truth whenever the week or the
  // parent-owned my-status changes. A live draft is only preserved when the
  // server has no pick for this week AND the draft belongs to this week's
  // games (so stale drafts never survive a week switch). A draft left behind on
  // a week the user switched AWAY from isn't lost by that: it lives in the week
  // cache and the switch-back hydration puts it back before this runs.
  useEffect(() => {
    if (!isSurvivor) return;
    setWeekPick((prev) =>
      resolveSurvivorDraft(prev, savedPickForRound(undercardId), undercard, undercardId),
    );
  }, [isSurvivor, undercardId, savedPickForRound, undercard]);

  // Survivor counterpart of the locked-snap reconcile above: a draft that differs
  // from the saved pick becomes invalid once either game locks (changing a pick
  // requires BOTH the old and new games to be open), so snap back to the saved truth.
  useEffect(() => {
    if (!isSurvivor || !undercard || undercard.id !== undercardId) return;
    setWeekPick((prev) =>
      snapLockedWeekPick(prev, savedPickForRound(undercard.id), undercard, nowMs),
    );
  }, [isSurvivor, nowMs, undercard, undercardId, savedPickForRound]);

  // Report the live draft count so the parent's week rail dashes fill in as
  // the user picks (mirrors the old inline stepper's live overlay). Gated on
  // the loaded week matching the selected one: on a week switch this component
  // renders once with the new id and the OLD draft map, which would otherwise
  // flash the previous week's count onto the new week's rail dash.
  useEffect(() => {
    if (!isSurvivor && undercard?.id === undercardId) {
      onDraftCountChange?.(undercardId, draftPicks.size);
    }
  }, [isSurvivor, undercardId, undercard, draftPicks, onDraftCountChange]);

  // Keyed, unconditional write — see mergeWeekCacheEntry for the merge rules
  // (weekPick merges on undefined so a cleared survivor draft isn't
  // resurrected; `complete` is never demoted by a later partial write).
  const writeCache = useCallback((entry: Partial<WeekCacheEntry> & { id: string }) => {
    const merged = mergeWeekCacheEntry(cacheRef.current.get(entry.id), entry);
    if (merged) cacheRef.current.set(entry.id, merged);
  }, []);

  const fetchData = useCallback(async (opts?: { background?: boolean }) => {
    if (!eventId || !undercardId) return;
    const background = opts?.background ?? false;
    // WeekView stays mounted across week switches, so a slow load can outlive
    // the week that started it. Every write is either keyed (the cache — always
    // safe, and it's what a switch back reads) or guarded by isStale(), which
    // is what stops week 1's payload from landing on week 2's screen.
    const forUndercardId = undercardId;
    const isStale = () => isStaleContext(undercardIdRef.current, forUndercardId);
    // A non-OK cached fetch must not produce a "complete" entry — the week
    // would then be served from cache with empty picks until a remount.
    let payloadComplete = true;
    try {
      if (!background) setLoading(true);
      const ucRes = await fetch(`${API_BASE}/api/main-events/${eventId}/undercards/${forUndercardId}`);
      let uc: UndercardDetail | null = null;
      if (ucRes.ok) {
        uc = await ucRes.json();
        if (uc) writeCache({ id: forUndercardId, undercard: uc });
        if (isStale()) return;
        setUndercard(uc);
      } else if (!background && !isStale()) {
        // Store accessor (not the useToast helpers): fetchData is a useCallback
        // and the per-render helper identities would churn its deps.
        useUIStore.getState().addToast({ type: 'error', title: 'Week not found', haptic: 'error' });
      }

      if (background) return; // cache hit — only refresh the game/score data

      // Fetch prop limits (works for both auth and unauth) — survivor has no props.
      // Card leagues: this endpoint also returns the roster-filtered prop list.
      if (!isSurvivor) {
        const propsInfoRes = isLoggedIn()
          ? await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/props`)
          : await fetch(`${API_BASE}/api/main-events/${eventId}/undercards/${forUndercardId}/props`);
        if (propsInfoRes.ok) {
          const propsData = await propsInfoRes.json();
          writeCache({
            id: forUndercardId,
            maxProps: propsData.maxProps ?? 5,
            purchasedCount: propsData.purchasedCount ?? 0,
          });
          if (isStale()) return;
          setMaxProps(propsData.maxProps ?? 5);
          setPurchasedCount(propsData.purchasedCount ?? 0);
          if (isCardLeague) setBbProps(propsData.props ?? []);
        }
      }

      if (isLoggedIn() && !isSurvivor) {
        // Card leagues have no game picks — skip my-picks.
        if (!isCardLeague) {
          const picksRes = await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/my-picks`);
          if (!picksRes.ok) payloadComplete = false;
          if (picksRes.ok) {
            const picks: GamePickData[] = await picksRes.json();
            const map = new Map<string, PickSelection>();
            picks.forEach((p) => map.set(p.gameId, p.pickedWinner));
            writeCache({ id: forUndercardId, savedPicks: picks, draftPicks: map });
            if (isStale()) return;
            setSavedPicks(picks);
            setDraftPicks(map);
          }
        }
        const propsRes = await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/my-props`);
        if (!propsRes.ok) payloadComplete = false;
        if (propsRes.ok) {
          const props: MyPropData[] = await propsRes.json();
          writeCache({ id: forUndercardId, myProps: props });
          if (isStale()) return;
          setMyProps(props);
        }
      }

      // Every cached field for this week has now landed (survivor and
      // logged-out users simply skip the fetches above), so the entry is
      // finally safe to read as a hit. Reaching this line is the point: a load
      // that bailed on isStale() above leaves the entry incomplete, and the
      // next visit re-loads it in full instead of trusting a half-written one.
      // Card-league state (roster/lineup below) is deliberately outside the
      // cached shape — those formats bypass the cache entirely.
      if (payloadComplete) writeCache({ id: forUndercardId, complete: true });

      // Card-league lineup panel: this week's scored rows, else the roster
      // preview (Best Ball) / chosen-or-default lineup (DRAFT_PPR). Gated on
      // entry (ref — hasEntered isn't a fetchData dep) so not-entered visitors
      // never fire a 404ing my-roster request.
      if (isCardLeague && isLoggedIn() && uc && hasEnteredRef.current) {
        try {
          const wsRes = await authFetch(
            `/api/main-events/${eventId}/roster/weekly-scores?week=${uc.round}`,
          );
          if (isStale()) return;
          if (wsRes.ok) {
            const data = await wsRes.json();
            const week: WeeklyScoreData | undefined = (data.weeks ?? [])[0];
            setBbWeek(week ?? null);
            if (!week && !isDraftPpr) {
              // Best Ball pre-scoring preview — DRAFT_PPR previews via lineup.
              const rosterRes = await authFetch(`/api/main-events/${eventId}/roster/my-roster`);
              const roster = rosterRes.ok ? await rosterRes.json() : null;
              if (isStale()) return;
              setBbRoster(roster);
            } else {
              setBbRoster(null);
            }
          } else {
            setBbWeek(null);
            setBbRoster(null);
          }
        } catch {
          if (isStale()) return;
          setBbWeek(null);
          setBbRoster(null);
        }

        // DRAFT_PPR: the chosen-or-default lineup (editor while editable, the
        // frozen chosen lineup once the week has locked but not yet scored).
        if (isDraftPpr) {
          try {
            const luRes = await authFetch(
              `/api/main-events/${eventId}/roster/lineup?week=${uc.round}`,
            );
            const lu = luRes.ok ? await luRes.json() : null;
            if (isStale()) return;
            setLineup(lu);
          } catch {
            if (isStale()) return;
            setLineup(null);
          }
        }
      }
    } catch {
      if (!background && !isStale()) {
        useUIStore.getState().addToast({ type: 'error', title: 'Failed to load week', haptic: 'error' });
      }
    } finally {
      // A stale load must never clear the flag — the week that replaced it owns
      // `loading` now, and flipping it here would flash "Week not found".
      if (!background && !isStale()) setLoading(false);
    }
  }, [eventId, undercardId, isSurvivor, isCardLeague, isDraftPpr, writeCache]);

  // Week switch (or login/event change): hydrate from cache when available so
  // the switch is instant, then refresh game data in the background; otherwise
  // do a full load. Login and event changes clear the cache — picks are
  // per-user and per-event.
  const lastTokenRef = useRef(accessToken);
  const lastEventIdRef = useRef(eventId);
  useEffect(() => {
    if (lastTokenRef.current !== accessToken || lastEventIdRef.current !== eventId) {
      lastTokenRef.current = accessToken;
      lastEventIdRef.current = eventId;
      cacheRef.current.clear();
    }
    // Card leagues bypass the week cache — their lineup/props state lives
    // outside the cached shape and refetches are cheap (no picks payload).
    // Only a COMPLETE entry is a hit. A load that went stale mid-flight left
    // one holding an undercard and no picks; treating that as a hit would run
    // the background refresh, which returns right after the undercard, and the
    // user's saved picks would stay missing until a full remount.
    const cached = isCardLeague ? undefined : cacheRef.current.get(undercardId);
    if (isCacheHit(cached)) {
      setUndercard(cached.undercard);
      setSavedPicks(cached.savedPicks);
      setDraftPicks(cached.draftPicks);
      // Survivor: server truth first, this week's unsaved draft otherwise —
      // the same precedence the sync effect applies a commit later, applied
      // here too so a saved pick never flashes as an unsaved draft.
      setWeekPick(savedPickForRound(undercardId) ?? cached.weekPick);
      setMyProps(cached.myProps);
      setMaxProps(cached.maxProps);
      setPurchasedCount(cached.purchasedCount);
      setLoading(false);
      fetchData({ background: true });
    } else {
      setUndercard(null);
      setSavedPicks([]);
      setDraftPicks(new Map());
      setMyProps([]);
      setBbProps([]);
      setBbWeek(null);
      setBbRoster(null);
      setLineup(null);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undercardId, eventId, accessToken, fetchData]);

  // After entering the league (page-level modal flips hasEntered), refetch so
  // the roster panel + roster-filtered props appear without a week switch.
  useEffect(() => {
    const was = hasEnteredRef.current;
    hasEnteredRef.current = hasEntered;
    if (isCardLeague && hasEntered && !was) fetchData();
  }, [isCardLeague, hasEntered, fetchData]);

  // Keep the draft cache current so switching away and back preserves it —
  // survivor's weekPick included, so both formats behave the same way.
  // Gated on the loaded week matching the selected one: mid-switch this
  // component renders with the NEW id and the OLD drafts, and that pairing must
  // never be written under the new week's key (it's also what stops the switch
  // commit from writing a not-yet-rehydrated null over a cached draft).
  useEffect(() => {
    if (undercard?.id === undercardId) writeCache({ id: undercardId, draftPicks, weekPick });
  }, [draftPicks, weekPick, undercard, undercardId, writeCache]);

  // JestSDK notification scheduling — the undercard-scoped effect from the old
  // picks page, kept functional on the event page. Waits until this week's
  // data has finished loading (and matches the selected week) so stale picks
  // from another undercard can't schedule/skip prop_lock incorrectly.
  useEffect(() => {
    if (!undercard || loading || undercard.id !== undercardId) return;
    const sdk = typeof window !== 'undefined' ? window.JestSDK : null;
    // Saved picks > 0 → the user has progressed past the "needs reminder"
    // state, so the helper will skip prop_lock and clear any prior dedupe.
    const hasMadePicks = savedPicks.length > 0;
    void scheduleUndercardNotifications(sdk, undercard, {
      hasEntry: hasEntered,
      hasMadePicks,
    });
  }, [undercard, undercardId, hasEntered, savedPicks, loading]);

  const handlePick = (gameId: string, winner: PickSelection) => {
    if (isSurvivor) {
      // One pick per week — same game+side toggles off, anything else replaces.
      setWeekPick((prev) =>
        prev && prev.gameId === gameId && prev.side === winner ? null : { gameId, side: winner }
      );
      return;
    }
    setDraftPicks((prev) => {
      const next = new Map(prev);
      // Switch-only once banked: submitPicks upserts with no delete, so a
      // cleared saved pick could never persist — it would leave Save
      // permanently dirty against a row the server keeps re-sending.
      if (next.get(gameId) === winner) {
        if (savedPicks.some((p) => p.gameId === gameId)) return prev;
        next.delete(gameId);
      } else {
        next.set(gameId, winner);
      }
      return next;
    });
  };

  const handleClearPicks = () => {
    if (isSurvivor) {
      setWeekPick(savedWeekPick);
      return;
    }
    // Reset to saved picks (or empty if none saved)
    const map = new Map<string, PickSelection>();
    savedPicks.forEach((p) => map.set(p.gameId, p.pickedWinner));
    setDraftPicks(map);
  };

  // ── Instant save ──────────────────────────────────────────────────────────
  // A 2xx from the pick endpoints IS the truth. The drafts the server accepted
  // are promoted to saved state (and toasted) synchronously; refreshEntry() and
  // the my-picks / my-status refetches run fire-and-forget behind it. They only
  // reconcile where they DISAGREE, so a confirmed pick never visibly reverts.

  /**
   * Background reconcile after an accepted save. my-picks is authoritative for
   * everything EXCEPT a pick we just watched the server accept: an accepted
   * (non-skipped) pick always wins over this refetch. A lagging read that comes
   * back missing one — or still carrying the PREVIOUS winner for a game the user
   * just switched — is overwritten, so a confirmed save never visibly reverts.
   * Drafts are corrected only where the server disagrees AND the user hasn't
   * changed that game since the save.
   *
   * WeekView survives week switches, so the round-trip can outlive the week it
   * was fired for. The cache write is keyed and always lands; the setStates are
   * NOT keyed, so they bail if the user has moved on — otherwise week 1's rows
   * would stomp week 2's live state.
   */
  const reconcileSavedPicks = (
    sent: { gameId: string; pickedWinner: PickSelection }[],
    skipped: string[],
  ) => {
    const forUndercardId = undercardId;
    void (async () => {
      const picksRes = await authFetch(
        `/api/main-events/${eventId}/undercards/${forUndercardId}/my-picks`,
      );
      if (!picksRes.ok) return;
      const fresh: GamePickData[] = await picksRes.json();
      const skippedSet = new Set(skipped);
      // Skipped picks never persisted, so they stay whatever the read says;
      // everything else the server took overwrites the row it came back with.
      const merged = mergeSavedPicks(
        fresh,
        sent.filter((p) => !skippedSet.has(p.gameId)),
      );
      // Keyed — safe whether or not this is still the visible week, and it's
      // what the user gets when they switch back.
      writeCache({ id: forUndercardId, savedPicks: merged });
      // Moved on mid-flight: the cache above already holds the truth.
      if (isStaleContext(undercardIdRef.current, forUndercardId)) return;
      setSavedPicks(merged);
      setDraftPicks((prev) => reconcileDraftsAfterSave(prev, sent, merged));
    })();
  };

  // POST the single survivor pick. The 200 promotes the pick into the
  // parent-owned my-status immediately (header/rail glyphs + the action bar's
  // saved state); my-status refetches behind it to reconcile. Throws a
  // user-friendly Error mapped from the API's error codes.
  const postSurvivorPick = async (pick: { gameId: string; side: 'HOME' | 'AWAY' }): Promise<string> => {
    const forEventId = eventId;
    // Every status write below sits after an await; none may land on a
    // different event than the one the pick was made in.
    const setStatusIfCurrent = (st: SurvivorStatusData | null) => {
      if (isStaleContext(eventIdRef.current, forEventId)) return;
      onSurvivorStatusChange(st);
    };
    const res = await authFetch(`/api/main-events/${forEventId}/survivor/pick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ undercardId, gameId: pick.gameId, side: pick.side }),
    });
    const data = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok) {
      const code = (data as { code?: string }).code;
      if (code === 'TEAM_ALREADY_USED') {
        // Refresh burned teams from the error body so the cards gray out immediately.
        const used: SurvivorUsedTeam[] = (data as { usedTeams?: SurvivorUsedTeam[] }).usedTeams ?? [];
        if (survivorStatus) setStatusIfCurrent({ ...survivorStatus, usedTeams: used });
        const game = undercard?.games.find((g) => g.id === pick.gameId);
        const team = game ? (pick.side === 'HOME' ? game.homeTeam : game.awayTeam) : 'that team';
        const week = used.find((u) => u.team === team)?.round;
        // week != null: round 0 (Preseason Week 3) is a real, truthy-falsy week.
        throw new Error(
          week != null
            ? `You already used ${team} in ${week > 0 ? `Week ${week}` : 'a preseason week'}`
            : `You already used ${team}`
        );
      }
      if (code === 'GAME_LOCKED') throw new Error('That game has kicked off — pick locked.');
      if (code === 'PICK_LOCKED') throw new Error('Your pick is already locked for this week.');
      if (code === 'ENTRY_ELIMINATED') throw new Error("You've been eliminated — you can still watch the pool.");
      throw new Error((data as { error?: string }).error || 'Failed to save pick');
    }
    const saved = data as { team: string; lockTime?: string };
    // The server took the pick — record it against the round it was made for.
    // This is the ONLY promotion that runs in all four (entered × my-status null)
    // combinations, so a 200 always reads as saved; the status writes below are
    // what additionally keep the header/rail glyphs honest when we can do them
    // safely. Guarded like every other post-await write: never land on a
    // different event than the pick was made in.
    if (!isStaleContext(eventIdRef.current, forEventId)) {
      setConfirmedWeekPick({ undercardId, gameId: pick.gameId, side: pick.side });
    }
    // Promote the accepted pick into my-status now so the bar flips to saved and
    // the card keeps its highlight without waiting on a round-trip.
    if (undercard?.id === undercardId) {
      const game = undercard.games.find((g) => g.id === pick.gameId);
      const optimistic: SurvivorPickInfo = {
        round: undercard.round,
        undercardId,
        gameId: pick.gameId,
        side: pick.side,
        team: saved.team,
        result: 'PENDING',
        lockTime: new Date(saved.lockTime ?? game?.lockTime ?? undercard.lockTime),
      };
      if (survivorStatus) {
        setStatusIfCurrent({
          ...survivorStatus,
          // Changing the pick releases the previous team for this week, so the
          // round is rebuilt rather than appended to (matches the server).
          picksByWeek: [
            ...survivorStatus.picksByWeek.filter((p) => p.undercardId !== undercardId),
            optimistic,
          ],
          usedTeams: [
            ...survivorStatus.usedTeams.filter((u) => u.round !== undercard.round),
            { team: saved.team, round: undercard.round },
          ],
        });
      } else if (!hasEntered) {
        // Enter & Pick in one action — a genuinely fresh entrant has no
        // my-status yet, so a zero-week shell is accurate. The refetch below
        // replaces it with the real thing.
        setStatusIfCurrent({
          status: 'ALIVE',
          eliminatedWeek: null,
          weeksSurvived: 0,
          currentPick: optimistic,
          picksByWeek: [optimistic],
          usedTeams: [{ team: saved.team, round: undercard.round }],
        });
      }
      // Entered but status null = the parent's my-status fetch failed. Writing
      // the shell there would erase every previously burned team (usedTeams
      // drives the burned-button state) until a refetch landed — so leave it
      // null and let the refetch below populate the real history. The pick still
      // reads as saved: confirmedWeekPick above covers the draft comparison
      // without inventing a status the client can't know.
    }
    void (async () => {
      const stRes = await authFetch(`/api/main-events/${forEventId}/survivor/my-status`);
      if (stRes.ok) setStatusIfCurrent(await stRes.json());
    })();
    return saved.team;
  };

  const handleEnterAndSave = async () => {
    // Week-guarded like every other post-await path: this enter+save belongs to
    // the week it was fired from. Without it a mid-request week switch merges
    // these picks into the NEW week's state and caches them under its key.
    const forUndercardId = undercardId;
    const isStale = () => isStaleContext(undercardIdRef.current, forUndercardId);
    if (!isLoggedIn()) {
      router.push('/login');
      return;
    }
    // Resolves once the parent's entry state has caught up with the server. The
    // finally below waits on THIS (never on the picks) before re-enabling Enter.
    let entrySynced: Promise<void> | null = null;
    try {
      setEntering(true);

      // Enter the event
      const enterBody = accessType === 'CODE_REQUIRED' ? { accessCode } : undefined;
      const enterRes = await authFetch(`/api/main-events/${eventId}/enter`, {
        method: 'POST',
        ...(enterBody ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(enterBody),
        } : {}),
      });
      if (!enterRes.ok) {
        const data = await enterRes.json().catch(() => ({} as { error?: string; code?: string }));
        // event closed (last game locked) while the page sat open on a stale
        // joinable snapshot — hide Enter so it can't be retried into the same error.
        if (data.code === 'EVENT_NOT_OPEN') onJoinableChange(false);
        if (data.code === 'SURVIVOR_JOIN_CLOSED') {
          onJoinableChange(false);
          throw new Error('Pool locked — Week 1 has started.');
        }
        throw new Error(data.error || 'Failed to enter event');
      }
      // Sync the parent-owned entry state (flips hasEntered → true everywhere).
      // The server already created the entry, so the pick save below no longer
      // waits on this client-side refresh — it lands on its own. The promise is
      // kept only so the finally can hold the Enter button disabled until
      // hasEntered has actually flipped.
      entrySynced = refreshEntry().catch(() => {});
      // A PAID entry just debited coins server-side — refresh the nav balance,
      // same as the header enter path and the Best Ball confirm.
      void refreshBalance();
      // The entry landed — same win moment as every other entry path (toast +
      // gold ring). Fired here, before the pick save, because the entry is real
      // whether or not the picks that follow succeed. The per-save toasts below
      // stay save-specific so the two never say the same thing twice. The
      // callback is optional, so keep a plain acknowledgement for hosts that
      // don't wire it.
      if (onEntered) onEntered();
      else success('Entered event!');

      if (isSurvivor) {
        if (weekPick) {
          const team = await postSurvivorPick(weekPick);
          success(`Pick saved — ${team}`);
        }
      // If user has picks selected, save them too
      } else if (draftPicks.size > 0) {
        const picks = Array.from(draftPicks.entries()).map(([gameId, pickedWinner]) => ({
          gameId,
          pickedWinner,
        }));
        const res = await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/picks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ picks }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to submit picks');
        }
        const result = await res.json().catch(() => ({}));
        const skipped: string[] = result.skippedGameIds ?? result.data?.skippedGameIds ?? [];
        const savedCount: number = result.picksCount ?? result.data?.picksCount ?? 0;
        if (savedCount > 0) {
          void markFirstMilestoneOnce(typeof window !== 'undefined' ? window.JestSDK : null);
        }
        // Instant: the accepted picks are saved state as of this 200. Folded in
        // functionally (and off the ref for the cache copy) so a reconcile that
        // landed while this POST was in flight isn't undone by a stale closure.
        const skippedSet = new Set(skipped);
        const accepted = picks.filter((p) => !skippedSet.has(p.gameId));
        // Off the ref only while this is still the visible week — otherwise the
        // ref holds the OTHER week's rows and would merge the two together.
        const base = isStale() ? [] : savedPicksRef.current;
        const optimistic = mergeSavedPicks(base, accepted);
        // Keyed, so it's right either way and is what a switch-back reads.
        writeCache({ id: forUndercardId, savedPicks: optimistic });
        if (!isStale()) setSavedPicks((prev) => mergeSavedPicks(prev, accepted));
        // Reconcile any games that locked between pick and save.
        if (skipped.length > 0 && !isStale()) {
          setDraftPicks((prev) => reconcileSkippedDrafts(prev, skipped, optimistic));
        }
        void refreshEntry();
        reconcileSavedPicks(picks, skipped);
        // Partial saves (some games locked between pick and save) warn; clean
        // saves succeed. The entry itself is already toasted by onEntered above,
        // so this copy only ever talks about the picks.
        if (skipped.length > 0) {
          warning(
            savedCount > 0
              ? `Saved ${savedCount} — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
              : `Too late — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
          );
        } else {
          success('Picks saved!');
        }
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to enter');
    } finally {
      // `entering` is what disables Enter; `hasEntered` (which hides it) only
      // flips when refreshEntry lands. Clearing first re-enables Enter on an
      // already-entered event, so a fast second tap eats an error. Wait for the
      // ENTRY sync only — the pick save above stays fire-and-forget.
      if (entrySynced) await entrySynced;
      setEntering(false);
    }
  };

  const handleSubmitPicks = async () => {
    // Same rule as the loader, the reconcile and the prop purchase: this save
    // belongs to the week it was fired from. Without the capture a mid-request
    // week switch merges these accepted picks into the NEW week's saved state
    // and caches them under the new week's key.
    const forUndercardId = undercardId;
    const isStale = () => isStaleContext(undercardIdRef.current, forUndercardId);
    // Press-time confirmation fires only once a save is actually starting —
    // after the auth guard, so a signed-out tap navigates to /login instead of
    // buzzing on the way out. The success/warning toast fires its own pattern
    // when the save lands.
    if (isSurvivor) {
      if (!weekPick) return;
      if (!isLoggedIn()) {
        router.push('/login');
        return;
      }
      fire('impactMedium');
      try {
        setSubmitting(true);
        const team = await postSurvivorPick(weekPick);
        success(`Pick saved — ${team}`);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Failed to save pick');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (draftPicks.size === 0) return;
    if (!isLoggedIn()) {
      router.push('/login');
      return;
    }
    fire('impactMedium');
    try {
      setSubmitting(true);
      const picks = Array.from(draftPicks.entries()).map(([gameId, pickedWinner]) => ({
        gameId,
        pickedWinner,
      }));
      const res = await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ picks }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to submit picks');
      }
      const result = await res.json().catch(() => ({}));
      const skipped: string[] = result.skippedGameIds ?? result.data?.skippedGameIds ?? [];
      const savedCount: number = result.picksCount ?? result.data?.picksCount ?? 0;
      if (savedCount > 0) {
        void markFirstMilestoneOnce(typeof window !== 'undefined' ? window.JestSDK : null);
      }
      // Instant: every pick the server didn't skip is saved state as of this 200,
      // so the bar flips to "All saved" and the cards hold their picks now.
      // Folded in functionally (and off the ref for the cache copy) so a
      // reconcile that landed while this POST was in flight isn't undone by a
      // stale render closure.
      const skippedSet = new Set(skipped);
      const accepted = picks.filter((p) => !skippedSet.has(p.gameId));
      // Cache off the ref only when this is still the visible week — otherwise
      // savedPicksRef holds the OTHER week's rows and would merge them together.
      const base = isStale() ? [] : savedPicksRef.current;
      const optimistic = mergeSavedPicks(base, accepted);
      // Keyed, so it's correct either way and is what a switch-back reads.
      writeCache({ id: forUndercardId, savedPicks: optimistic });
      if (!isStale()) setSavedPicks((prev) => mergeSavedPicks(prev, accepted));
      // Reconcile games the server skipped (locked between pick and save) back to the
      // real saved state so a locked card stops showing a pick that never persisted.
      if (skipped.length > 0 && !isStale()) {
        setDraftPicks((prev) => reconcileSkippedDrafts(prev, skipped, optimistic));
      }
      // Rail dash counts come from my-entry's picksSubmitted — refresh them.
      void refreshEntry();
      reconcileSavedPicks(picks, skipped);
      // Partial saves (some games locked between pick and save) warn; clean saves succeed.
      if (skipped.length > 0) {
        warning(
          savedCount > 0
            ? `Saved ${savedCount} — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
            : `Too late — ${skipped.length} game${skipped.length > 1 ? 's' : ''} already locked`
        );
      } else {
        success('Picks saved!');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchaseProp = async (propId: string, userSelection?: 'YES' | 'NO') => {
    if (!isLoggedIn()) {
      router.push('/login');
      return;
    }
    if (!hasEntered) {
      showError('You must enter the event before purchasing props');
      return;
    }
    // Block double-clicks and purchases of a second prop while one is pending.
    if (inFlightProps.current.size > 0) return;
    inFlightProps.current.add(propId);
    // Same rule as the loader and the pick reconcile: every post-await setState
    // belongs to the week this purchase was fired from. Cache writes stay keyed
    // (they're correct whether or not this is still the visible week, and it's
    // what a switch back reads); live state is guarded, or week 1's prop rows
    // would stomp week 2's counter after a mid-purchase week swap.
    const forUndercardId = undercardId;
    const isStale = () => isStaleContext(undercardIdRef.current, forUndercardId);
    try {
      setPurchasingPropId(propId);
      const body: Record<string, unknown> = { propId };
      if (userSelection) body.userSelection = userSelection;
      const res = await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/props/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to purchase prop');
      }
      // Refetch the balance so the header coins reflect the spend.
      void refreshBalance();
      // Optimistic on success: the counter moves and the toast fires now — the
      // my-props refetch below reconciles to the authoritative rows (and is the
      // only thing that can correct the count, never a precondition for it).
      if (!isStale()) setPurchasedCount((prev) => prev + 1);
      success(userSelection ? `Picked ${userSelection}!` : 'Prop purchased!');
      const propsRes = await authFetch(`/api/main-events/${eventId}/undercards/${forUndercardId}/my-props`);
      if (propsRes.ok) {
        const fresh: MyPropData[] = await propsRes.json();
        writeCache({ id: forUndercardId, myProps: fresh, purchasedCount: fresh.length });
        if (!isStale()) {
          setMyProps(fresh);
          setPurchasedCount(fresh.length);
        }
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to purchase');
    } finally {
      setPurchasingPropId(null);
      inFlightProps.current.delete(propId);
    }
  };

  if (loading) {
    // Ghost of what's coming (countdown line + first carousel card) rather than a
    // spinner — same widths as the real thing, so nothing jumps when data lands.
    return (
      <div aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading this week</span>
        <div className="mb-6 h-5 w-40 rounded-md bg-surface-2 mint-shimmer" aria-hidden="true" />
        <div className={cn('flex gap-4', CAROUSEL_INSET)} aria-hidden="true">
          <div className="shrink-0 w-[92%] max-w-[380px] h-56 rounded-xl bg-surface-2 mint-shimmer" />
        </div>
      </div>
    );
  }

  if (!undercard) {
    return (
      <div className="flex items-center justify-center py-24">
        <p className="text-warm-gray">Week not found</p>
      </div>
    );
  }

  const isGameLocked = (game: GameData) => isGameLockedAt(game, undercard, nowMs);
  // Terminal statuses lock the round regardless of its games (also covers a
  // zero-game round, where allGamesLocked would be false); LOCKED hard-locks only
  // in WHOLE_UNDERCARD. Mirrors the hardLocked branch of isGameLockedAt.
  const roundStatusLocked =
    undercard.status === 'SCORING' ||
    undercard.status === 'COMPLETED' ||
    (undercard.lockMode === 'WHOLE_UNDERCARD' && undercard.status === 'LOCKED');
  // Page-level gate (action bar / countdown) — true when the round is status-locked
  // or EVERY game is locked. Per-card locking is applied on each UnifiedGameCard below.
  const allGamesLocked = undercard.games.length > 0 && undercard.games.every(isGameLocked);
  const picksDisabled = roundStatusLocked || allGamesLocked;
  // Round-wide / standalone props lock at the undercard's single lockTime, mirroring
  // the server: SCORING/COMPLETED always; LOCKED only in WHOLE_UNDERCARD; else by time.
  const roundWideLocked = roundStatusLocked || nowMs >= new Date(undercard.lockTime).getTime();
  // Next upcoming game lock, for the countdown (PER_GAME shows the next game to lock).
  const nextLockTime =
    undercard.lockMode === 'PER_GAME'
      ? (undercard.games
          .map((g) => new Date(g.lockTime))
          .filter((d) => d.getTime() > nowMs)
          .sort((a, b) => a.getTime() - b.getTime())[0] ?? null)
      : new Date(undercard.lockTime);

  const isOpenGame = (gameId: string) => {
    const g = undercard.games.find((x) => x.id === gameId);
    return !!g && !isGameLocked(g);
  };

  // Save the chosen 8 for this week (DRAFT_PPR). Throws LineupSaveError so the
  // editor renders code-specific copy; a LINEUP_WEEK_LOCKED failure refetches
  // the lineup so the UI flips to the frozen read-only panel.
  const handleSaveLineup = async (starterCardIds: string[], slots: Record<string, string>) => {
    // Week the editor was showing when Save was pressed — the round-trip can
    // outlive it now that WeekView persists across switches.
    const forUndercardId = undercardId;
    const isStale = () => isStaleContext(undercardIdRef.current, forUndercardId);
    const res = await authFetch(`/api/main-events/${eventId}/roster/lineup`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ week: undercard.round, starters: starterCardIds, slots }),
    });
    const data = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok) {
      if ((data as { code?: string }).code === 'LINEUP_WEEK_LOCKED') {
        const luRes = await authFetch(
          `/api/main-events/${eventId}/roster/lineup?week=${undercard.round}`,
        );
        if (luRes.ok) {
          const lu = await luRes.json();
          if (!isStale()) setLineup(lu);
        }
      }
      throw new LineupSaveError(data as { error?: string; code?: string });
    }
    if (isStale()) return;
    // The PUT responds with the same enriched shape GET returns.
    setLineup(data as unknown as WeeklyLineupData);
    success('Lineup saved!');
  };

  // ── Card-league third mode (BEST_BALL / DRAFT_PPR) — lineup panel/editor +
  // roster-filtered predictions. No picks, no action bar (entry happens via
  // the page-level roster modal).
  if (isCardLeague) {
    const purchasedPropIds = new Set(myProps.map((p) => p.undercardPropId));
    const atLimit = purchasedCount >= maxProps;
    const propDisabled = roundWideLocked || !hasEntered;
    const isScoredWeek = undercard.status === 'SCORING' || undercard.status === 'COMPLETED';
    // DRAFT_PPR pre-lock editor: server-side editable AND the effective first
    // kickoff hasn't passed on the live tick (flips read-only without refresh).
    const showEditor =
      isDraftPpr &&
      hasEntered &&
      !!lineup &&
      lineup.editable &&
      (!lineup.lockTime || new Date(lineup.lockTime).getTime() > nowMs);

    return (
      <WeekContentFade weekKey={undercardId}>
        {/* Countdown to the week's lock (the LineupEditor carries its own) */}
        {!roundWideLocked && !showEditor && (
          <div className="mb-6">
            <CountdownTimer lockTime={undercard.lockTime} />
          </div>
        )}

        {/* Weekly lineup — DRAFT_PPR edits pre-lock; scored rows once stats
            land; the frozen chosen lineup (PPR) / roster preview (Best Ball)
            in between */}
        {showEditor && lineup ? (
          <div className="mb-8">
            <LineupEditor
              key={undercard.round}
              lineup={lineup}
              week={undercard.round}
              onSave={handleSaveLineup}
            />
          </div>
        ) : hasEntered && bbWeek ? (
          <div className="mb-8">
            <RosterWeekPanel
              players={bbWeek.players}
              optimalPoints={bbWeek.optimalPoints}
              benchPoints={bbWeek.benchPoints}
              isOfficial={bbWeek.isOfficial}
              mode="scored"
              pointsLabel={isDraftPpr ? 'your pts' : undefined}
              benchmarkPoints={isDraftPpr ? bbWeek.benchmarkPoints : undefined}
            />
          </div>
        ) : hasEntered && isDraftPpr && lineup ? (
          <div className="mb-8">
            {/* Locked but not yet scored — the frozen chosen lineup, read-only */}
            <RosterWeekPanel players={lineup.players} mode="preview" />
          </div>
        ) : hasEntered && bbRoster ? (
          <div className="mb-8">
            <RosterWeekPanel players={bbRoster.players} mode="preview" />
          </div>
        ) : !hasEntered ? (
          <div className="mb-8 p-4 rounded-xl bg-surface-2 border border-gold/20 max-w-md">
            <p className="text-sm text-cream font-medium mb-1">No lineup yet</p>
            <p className="text-xs text-warm-gray">
              {joinable
                ? isDraftPpr
                  ? 'Enter the league from the header to lock in your 10-card roster — you set your starting 8 each week.'
                  : 'Enter the league from the header to lock in your 10-card roster — your best 8 score automatically each week.'
                : 'Entry is closed for this league.'}
            </p>
          </div>
        ) : null}

        {/* Roster-filtered predictions — unlike survivor, props stay */}
        {hasEntered && (
          <PurchasedPropsSummary
            myProps={myProps}
            purchasedCount={purchasedCount}
            maxProps={maxProps}
            sport={sport}
          />
        )}
        {bbProps.length > 0 && (
          <div className="space-y-3 max-w-md">
            <div className="flex items-center justify-between">
              <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase">
                Your Players — Predictions
              </span>
              {!isScoredWeek && (
                <span className="text-xs text-warm-gray">
                  {purchasedCount}/{maxProps} props selected
                </span>
              )}
            </div>
            {bbProps.map((prop) => {
              const purchased = myProps.find((p) => p.undercardPropId === prop.id) ?? null;
              return (
                <StandalonePropCard
                  key={prop.id}
                  prop={prop}
                  purchased={purchased}
                  onPurchase={handlePurchaseProp}
                  disabled={propDisabled || (atLimit && !purchasedPropIds.has(prop.id))}
                  purchasingPropId={purchasingPropId}
                />
              );
            })}
          </div>
        )}
        {hasEntered && bbProps.length === 0 && !isScoredWeek && (
          <p className="text-xs text-tertiary">
            No predictions available for your players this week.
          </p>
        )}
      </WeekContentFade>
    );
  }

  // Eliminated entrants can still browse the pool, but never pick again.
  const isEliminated = isSurvivor && survivorStatus?.status === 'ELIMINATED';

  // draftPicks is SEEDED from savedPicks on load, so a missing key is a real
  // deselection ("I no longer want this pick"), not "no opinion yet".
  const savedByGame = savedPicksByGame(savedPicks);

  // Does the draft differ from the saved truth? See hasUnsavedGamePicks — the
  // diff walks the UNION of saved and draft keys so a DELETION registers, and
  // it ignores games that are no longer open.
  const hasUnsavedPicks = isSurvivor
    ? hasUnsavedSurvivorPick(weekPick, savedWeekPick)
    : hasUnsavedGamePicks(savedByGame, draftPicks, isOpenGame);

  // Has any draft picks at all (for clear button)
  const hasDraftPicks = isSurvivor ? weekPick !== null : draftPicks.size > 0;

  // Teams the entrant has burned in OTHER weeks — the current week's own pick
  // is excluded so the user's live selection never renders as unavailable.
  const usedTeamsMap = new Map<string, number>();
  if (isSurvivor && survivorStatus) {
    for (const ut of survivorStatus.usedTeams) {
      if (ut.round === undercard.round) continue;
      usedTeamsMap.set(ut.team, ut.round);
    }
  }

  // Computed scores
  const pickPoints = savedPicks.reduce((sum, p) => sum + (p.pointsEarned ?? 0), 0);
  const propBonusPoints = myProps.reduce((sum, p) => sum + (p.bonusPoints ?? 0), 0);
  const correctPicks = savedPicks.filter((p) => p.isCorrect === true).length;
  const settledPicks = savedPicks.filter((p) => p.isCorrect !== null).length;
  const hitProps = myProps.filter((p) => p.result === 'HIT').length;
  const settledMyProps = myProps.filter((p) => p.result === 'HIT' || p.result === 'MISS').length;
  const isCompleted = undercard.status === 'COMPLETED';
  const accuracyMod = myEntryData?.accuracyModifier ?? 1.0;
  const totalScore = isCompleted && myEntryData ? myEntryData.totalScore : pickPoints + propBonusPoints;

  // Scored week — the header reads "Results" and every card is locked. The
  // track itself is the same carousel either way.
  const isResultsWeek = undercard.status === 'SCORING' || undercard.status === 'COMPLETED';

  // Floating action bar — Clear / Enter & Save / Sign in to Enter / Save Picks.
  // Clear covers the toggled-off state (draft empty, saved picks exist) in BOTH
  // formats so the saved truth can always be restored. The old `hasDraftPicks`
  // guard was survivor-excepted for exactly that reason; now that dirtiness sees
  // deletions it is redundant — hasUnsavedPicks already means Clear has work.
  const showClear = !picksDisabled && hasUnsavedPicks;
  // Entry is event-scoped: show Enter whenever the event is still joinable (any
  // round has an open game), even if THIS round is fully locked. The server
  // (enterEvent) enforces the same rule, so there is no client/server drift.
  const showEnter = joinable && !hasEntered && !!accessToken;
  const showSignIn = joinable && !accessToken && hasDraftPicks;
  const showSave = !picksDisabled && hasEntered && hasUnsavedPicks;
  // Progress meter — persistent for an entered user on a pick-able week (at least
  // one game still open). This is what keeps the bar on screen once everything is
  // saved; a fully locked / scored week drops it entirely.
  const showProgress = !isEliminated && hasEntered && !picksDisabled && undercard.games.length > 0;
  // Survivor is one pick per week, so its meter counts to 1 rather than to the
  // whole slate ("1/16 picked" would misread the format).
  const pickTotal = isSurvivor ? 1 : undercard.games.length;
  // One source of truth for "this game has a pick" — feeds both the action-bar
  // meter and the carousel mini-rail's complete state, so the two can't drift.
  // See effectivePickedGameIds: the draft owns an open game, the saved row owns
  // a locked one.
  const pickedGameIds: ReadonlySet<string> = isSurvivor
    ? survivorPickedGameIds(weekPick)
    : effectivePickedGameIds(undercard.games, savedByGame, draftPicks, isOpenGame);
  const pickedCount = Math.min(pickedGameIds.size, pickTotal);
  // Clean state: nothing pending AND something actually banked (a fresh entrant
  // with zero picks gets the nudge of an empty meter, not a "saved" badge).
  const showAllSaved =
    showProgress && !hasUnsavedPicks && (isSurvivor ? !!savedWeekPick : savedPicks.length > 0);
  // Eliminated entrants spectate — no actions, browsing stays open.
  const showActionBar = !isEliminated && (showClear || showEnter || showSignIn || showSave || showProgress);
  // Reserve the bar's footprint whenever it CAN appear — the height is constant,
  // so drafting or saving never reflows the page under the user. (Signed-out
  // joinable users get the space too, since their Sign-in CTA is draft-gated.)
  const reserveBarSpace =
    !isEliminated && (showActionBar || (joinable && !accessToken && !picksDisabled));

  return (
    <div className={cn(reserveBarSpace && 'pb-40 sm:pb-36')}>
      {/* Everything the week owns crossfades on a week switch; the fixed action
          bar below is a SIBLING of this wrapper, never a child (see
          WeekContentFade). */}
      <WeekContentFade weekKey={undercardId}>
      {/* No week heading here — the rail's active pill already names the week
          (removing "Week N / X games / N points/pick" pulls picks up-screen;
          per-card labels carry the points copy). */}

      {/* Countdown Timer — survivor counts to the saved pick's kickoff (else
          the pick deadline); standard counts to the next game to lock.
          "Your pick" until the game actually locks, then "Locked in". */}
      {!picksDisabled && !isEliminated && (
        isSurvivor && savedWeekPickInfo ? (
          <div className="mb-6 flex items-center gap-3 flex-wrap">
            <CountdownTimer lockTime={savedWeekPickInfo.lockTime} />
            <span className="text-xs text-warm-gray">
              {new Date(savedWeekPickInfo.lockTime).getTime() <= nowMs ? 'Locked in: ' : 'Your pick: '}
              <span className="text-gold font-bold">{savedWeekPickInfo.team}</span>
            </span>
          </div>
        ) : nextLockTime && (
          <div className="mb-6">
            <CountdownTimer lockTime={nextLockTime} />
          </div>
        )
      )}

      {/* Survivor banner — only when it adds signal beyond the header stat:
          ELIMINATED state, or an alive entrant with NO pick this week (nudge).
          Alive-with-pick shows nothing here — status lives in the header stat,
          the pick in the countdown line (dedupe, user feedback 2026-07-17). */}
      {hasEntered && isSurvivor && survivorStatus &&
        (survivorStatus.status === 'ELIMINATED' || (!savedWeekPickInfo && !isResultsWeek)) && (
        <SurvivorStatusBanner
          status={survivorStatus.status}
          weeksSurvived={survivorStatus.weeksSurvived}
          eliminatedWeek={survivorStatus.eliminatedWeek}
          currentPickTeam={savedWeekPickInfo?.team ?? null}
          currentPickLocked={!!savedWeekPickInfo && new Date(savedWeekPickInfo.lockTime).getTime() <= nowMs}
        />
      )}
      {hasEntered && !isSurvivor && (
        <ScoreBanner
          pickPoints={pickPoints}
          propBonusPoints={propBonusPoints}
          correctPicks={correctPicks}
          settledPicks={settledPicks}
          totalPicks={undercard.games.length}
          hitProps={hitProps}
          settledMyProps={settledMyProps}
          totalMyProps={myProps.length}
          isCompleted={isCompleted}
          accuracyModifier={accuracyMod}
          pointsPerCorrectPick={undercard.pointsPerCorrectPick}
          totalScore={totalScore}
        />
      )}

      {/* Share My Picks — surfaces the prediction-card image generator.
          Only shown when user has entered AND has picks/props locked in
          (survivor: this week's saved pick). Switches to "Winner Card"
          variant once the undercard is settled. */}
      {hasEntered && myEntryData && (isSurvivor ? !!savedWeekPickInfo : (myProps.length > 0 || savedPicks.length > 0)) && (
        <div className="mb-4 flex justify-end">
          <ShareCardButton
            undercardEntryId={myEntryData.undercardEntryId}
            variant={isCompleted ? 'winner' : 'prediction'}
          />
        </div>
      )}

      {/* Purchased Props Summary Tiles — no props in survivor */}
      {!isSurvivor && (
        <PurchasedPropsSummary
          myProps={myProps}
          purchasedCount={purchasedCount}
          maxProps={maxProps}
          sport={sport}
        />
      )}

      {/* Prediction Boosts — Unified Game Cards */}
      {(() => {
        const TEAM_CATEGORIES = new Set(['SPREAD', 'TOTAL', 'TOTAL_OVER', 'TOTAL_UNDER', 'MONEYLINE', 'TEAM_TOTAL_OVER', 'TEAM_TOTAL_UNDER']);
        const UNIFIED_CATEGORIES = new Set(['SPREAD', 'TOTAL', 'TOTAL_OVER', 'TOTAL_UNDER', 'MONEYLINE']);
        const purchasedPropIds = new Set(myProps.map((p) => p.undercardPropId));
        const atLimit = purchasedCount >= maxProps;
        // Round-wide / standalone props lock at the undercard lockTime.
        const propDisabledRoundWide = roundWideLocked || !hasEntered;

        // Manual props (admin-authored, propType !== 'ODDS_BASED') split:
        //   - per-game → embedded inside each UnifiedGameCard
        //   - round-wide (no gameId or gameId not in this undercard) → standalone section
        // Survivor is picks-only — feed the splitter nothing so every prop
        // section self-suppresses on empty.
        const activeProps = isSurvivor ? [] : undercard.props;
        const manualByGame = new Map<string, PropData[]>();
        const manualRoundWide: PropData[] = [];
        const oddsBased: PropData[] = [];
        const gameIdSet = new Set(undercard.games.map((g) => g.id));
        for (const prop of activeProps) {
          const isManual = prop.propType && prop.propType !== 'ODDS_BASED';
          if (!isManual) {
            oddsBased.push(prop);
            continue;
          }
          if (prop.gameId && gameIdSet.has(prop.gameId)) {
            const arr = manualByGame.get(prop.gameId) ?? [];
            arr.push(prop);
            manualByGame.set(prop.gameId, arr);
          } else {
            manualRoundWide.push(prop);
          }
        }

        // Build team props map for unified cards (SPREAD, TOTAL, MONEYLINE only)
        const unifiedByGame = new Map<string, PropData[]>();
        const playerByGame = new Map<string, PropData[]>();
        const standalone: PropData[] = [];

        for (const prop of oddsBased) {
          if (!prop.gameId) {
            standalone.push(prop);
            continue;
          }
          const isUnified = prop.category && UNIFIED_CATEGORIES.has(prop.category);
          const isTeam = !prop.category || TEAM_CATEGORIES.has(prop.category);
          if (isUnified) {
            const arr = unifiedByGame.get(prop.gameId) || [];
            arr.push(prop);
            unifiedByGame.set(prop.gameId, arr);
          } else if (!isTeam) {
            // Player prop
            const arr = playerByGame.get(prop.gameId) || [];
            arr.push(prop);
            playerByGame.set(prop.gameId, arr);
          } else {
            // Non-unified team props (TEAM_TOTAL etc.) — keep in unified card area for now
            const arr = unifiedByGame.get(prop.gameId) || [];
            arr.push(prop);
            unifiedByGame.set(prop.gameId, arr);
          }
        }

        const REGION_COLORS: Record<string, string> = {
          East: 'text-blue-400',
          West: 'text-red-400',
          South: 'text-green-400',
          Midwest: 'text-amber-400',
        };

        // Check if games have groups (regions)
        const hasGroups = undercard.games.some((g) => g.group);

        // Helper to render a UnifiedGameCard for a game (the track wrapper is
        // applied by MatchCarousel)
        const renderUnifiedCard = (game: GameData) => {
          const gameProps = unifiedByGame.get(game.id) || [];
          const gamePProps = playerByGame.get(game.id) || [];
          const gameManual = manualByGame.get(game.id) || [];
          // Per-card lock — each game locks at its own time in PER_GAME mode.
          const gameLocked = isGameLocked(game);
          const propDisabledForCard = gameLocked || !hasEntered;
          // At-limit only blocks new purchases; if user already bought any prop on this card,
          // the locked-state UI still needs to render. Apply the same logic for manual props.
          const hasOwnedHere =
            gameProps.some((p) => purchasedPropIds.has(p.id)) ||
            gameManual.some((p) => purchasedPropIds.has(p.id));
          return (
            <UnifiedGameCard
              game={game}
              pick={
                isSurvivor
                  ? (weekPick?.gameId === game.id ? weekPick.side : null)
                  : draftPicks.get(game.id) ?? null
              }
              onPick={handlePick}
              picksDisabled={gameLocked || isEliminated}
              pointsPerCorrectPick={undercard.pointsPerCorrectPick}
              showPoints={!isSurvivor}
              sport={sport}
              props={gameProps}
              playerProps={gamePProps}
              manualProps={gameManual}
              myProps={myProps}
              purchasedPropIds={purchasedPropIds}
              onPurchase={handlePurchaseProp}
              propDisabled={propDisabledForCard || (atLimit && !hasOwnedHere)}
              purchasingPropId={purchasingPropId}
              usedTeams={isSurvivor ? usedTeamsMap : undefined}
            />
          );
        };

        return (
          <div className="space-y-8">
            {/* No big section header / explainer — picks lead the screen. Boost
                guidance lives in a tooltip inside each card; the props counter
                stays as a compact right-aligned line. */}
            {(isResultsWeek || (!isSurvivor && undercard.props.length > 0 && !isResultsWeek)) && (
              <div className="flex items-center justify-between">
                {isResultsWeek ? (
                  <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase">Results</span>
                ) : (
                  <LineRefreshCountdown refreshAt={undercard.nextLineRefreshAt ?? null} />
                )}
                {!isSurvivor && undercard.props.length > 0 && !isResultsWeek && (
                  <span className="text-xs text-warm-gray">
                    {purchasedCount}/{maxProps} props selected
                  </span>
                )}
              </div>
            )}

            {/* Teams already burned this season — survivor only */}
            {isSurvivor && survivorStatus && (
              <UsedTeamsStrip usedTeams={survivorStatus.usedTeams} />
            )}

            {/* Unified Game Cards — horizontal scroll-snap track w/ dot stepper,
                open and scored weeks alike */}
            {!hasGroups ? (
              <MatchCarousel games={undercard.games} renderCard={renderUnifiedCard} pickedGameIds={pickedGameIds} />
            ) : (
              <div className="space-y-8">
                {(() => {
                  const groupOrder: string[] = [];
                  const groupMap = new Map<string, GameData[]>();
                  const ungrouped: GameData[] = [];
                  for (const game of undercard.games) {
                    const g = game.group || '';
                    if (!g) { ungrouped.push(game); continue; }
                    if (!groupMap.has(g)) { groupOrder.push(g); groupMap.set(g, []); }
                    groupMap.get(g)!.push(game);
                  }
                  return (
                    <>
                      {groupOrder.map((groupName) => {
                        const games = groupMap.get(groupName)!;
                        return (
                          <div key={groupName}>
                            <span className={cn('text-[10px] font-bold tracking-[0.2em] uppercase block mb-4', REGION_COLORS[groupName] || 'text-warm-gray/60')}>
                              {groupName}
                            </span>
                            <MatchCarousel games={games} renderCard={renderUnifiedCard} pickedGameIds={pickedGameIds} />
                          </div>
                        );
                      })}
                      {ungrouped.length > 0 && (
                        <MatchCarousel games={ungrouped} renderCard={renderUnifiedCard} pickedGameIds={pickedGameIds} />
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Player props are now integrated into UnifiedGameCard */}

            {/* Standalone props */}
            {standalone.length > 0 && (
              <div className="space-y-3">
                {standalone.map((prop) => {
                  const purchased = myProps.find((p) => p.undercardPropId === prop.id) ?? null;
                  return (
                    <StandalonePropCard
                      key={prop.id}
                      prop={prop}
                      purchased={purchased}
                      onPurchase={handlePurchaseProp}
                      disabled={propDisabledRoundWide || (atLimit && !purchased)}
                      purchasingPropId={purchasingPropId}
                    />
                  );
                })}
              </div>
            )}

            {/* Round-wide manual predictions (no gameId — apply to whole undercard) */}
            {manualRoundWide.length > 0 && (
              <div className="rounded-xl border border-surface-3 bg-surface-2 overflow-hidden w-full">
                <div className="px-4 py-2 bg-surface-3/50 border-b border-surface-3">
                  <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-warm-gray/70">
                    Round-wide Predictions
                    <span className="text-warm-gray/40 ml-2 normal-case tracking-normal font-normal">({manualRoundWide.length})</span>
                  </span>
                </div>
                <div className="p-2 space-y-0.5">
                  {manualRoundWide.map((prop) => (
                    <InlineManualPropRow
                      key={prop.id}
                      prop={prop}
                      gameSettled={false}
                      purchased={myProps.find((m) => m.undercardPropId === prop.id) ?? null}
                      onPurchase={handlePurchaseProp}
                      disabled={propDisabledRoundWide || (atLimit && !purchasedPropIds.has(prop.id))}
                      purchasingPropId={purchasingPropId}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}
      </WeekContentFade>

      {/* Persistent action bar — pinned just above the bottom tab bar. Nothing in
          this subtree's ancestry may take a transform/filter/perspective or the
          fixed positioning collapses into that ancestor — which is why it sits
          OUTSIDE WeekContentFade and why that crossfade is opacity-only. */}
      {showActionBar && (
        <div
          ref={actionBarRef}
          className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 border-t border-surface-3 bg-surface/95 backdrop-blur-sm"
        >
          {/* One row, always: 4.5rem = py-3 + the lg button's h-12, and
              flex-nowrap keeps that true down to 320px. Wrapping was worth ~41px
              of extra bar height on a 375px viewport — more than the pb the page
              reserves — so the meter gives space instead (its dash bar shrinks,
              its label goes sr-only) rather than the row breaking. */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 min-h-[4.5rem] flex flex-nowrap items-center gap-2 sm:justify-end">
            {/* Live pick progress — the count plus the week rail's own dash
                language (gold = picked, dim = not yet). Shrinkable, but only
                down to its own text: the buttons keep min-width:auto so their
                nowrap labels can never be squeezed, which leaves the dash bar
                as the one element that gives. */}
            {showProgress && (
              <div className="mr-auto flex items-center gap-2 shrink">
                {/* No aria-live: this re-announced on every tap, and the pick
                    buttons' own aria-pressed already carries the state. */}
                <span className="text-xs text-warm-gray whitespace-nowrap">
                  <span className="text-cream font-bold tabular-nums">{pickedCount}</span>
                  <span className="tabular-nums">/{pickTotal}</span>
                  {/* Visible from sm up, always present for screen readers —
                      "16/16" alone has to fit next to Clear + Save on mobile. */}
                  <span className="sr-only sm:not-sr-only"> picked</span>
                </span>
                <div className="flex items-center gap-[2px] w-14 sm:w-24 min-w-0 shrink" aria-hidden="true">
                  {Array.from({ length: pickTotal }).map((_, i) => (
                    <span
                      key={i}
                      className={cn(
                        'h-0.5 flex-1 min-w-0 rounded-full',
                        i < pickedCount ? 'bg-gold' : 'bg-cream/25',
                      )}
                    />
                  ))}
                </div>
              </div>
            )}

            {showClear && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearPicks}
                className="text-warm-gray hover:text-cream shrink-0"
              >
                Clear
              </Button>
            )}

            {showEnter && (
              <>
                {accessType === 'CODE_REQUIRED' && (
                  <input
                    type="text"
                    placeholder="Enter access code"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    className="px-3 py-2 rounded-lg bg-surface-2 border border-surface-3 text-cream placeholder-warm-gray/50 focus:border-gold focus:outline-none font-mono text-sm flex-1 min-w-0 sm:flex-none sm:w-48"
                  />
                )}
                <Button
                  variant="coins"
                  size="lg"
                  onClick={handleEnterAndSave}
                  disabled={entering || (accessType === 'CODE_REQUIRED' && !accessCode.trim())}
                  className="flex-1 sm:flex-none"
                >
                  {entering
                    ? 'Entering...'
                    : hasDraftPicks
                      ? isSurvivor ? 'Enter & Pick' : `Enter & Save (${draftPicks.size})`
                      : entryFee === 0 ? 'Enter Event' : `Enter (${entryFee} coins)`}
                </Button>
              </>
            )}

            {showSignIn && (
              <Button
                variant="coins"
                size="lg"
                onClick={() => router.push('/login')}
                className="flex-1 sm:flex-none"
              >
                Sign in to Enter
              </Button>
            )}

            {/* Save ⇄ "All saved" swap the same slot, so the bar never changes
                height and the transition reads as one control settling. */}
            <AnimatePresence mode="wait" initial={false}>
              {showSave ? (
                <motion.div
                  key="save"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex-1 sm:flex-none"
                >
                  <Button
                    variant="coins"
                    size="lg"
                    onClick={handleSubmitPicks}
                    disabled={submitting || (isSurvivor ? !weekPick : draftPicks.size === 0)}
                    className="w-full sm:w-auto"
                  >
                    {submitting
                      ? 'Saving...'
                      : isSurvivor ? 'Save Pick' : `Save Picks (${draftPicks.size})`}
                  </Button>
                </motion.div>
              ) : showAllSaved ? (
                <motion.div
                  key="all-saved"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="shrink-0"
                >
                  {/* h-12 = the Save button's height, so the bar keeps its
                      exact footprint through the swap. */}
                  <span className="inline-flex h-12 items-center gap-1.5 rounded-xl bg-verdigris/10 px-5 text-sm font-bold text-verdigris-text">
                    <span aria-hidden="true">✓</span> All saved
                  </span>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
